import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { css } from "../../styled-system/css";
import { HStack, VStack } from "../../styled-system/jsx";
import { Button } from "../components/core/basic/Button";
import { Modal } from "../components/core/basic/Modal";
import { Checkbox } from "../components/fields/base/Checkbox";
import { Select } from "../components/fields/base/Select";
import { TextAreaField } from "../components/fields/base/TextAreaField";
import { TextField } from "../components/fields/base/TextField";
import { useSubmitBugReport } from "../modules/admin/api/useSubmitBugReport";
import { useAppSelector } from "../redux/hooks";
import { successToast } from "../utils/toasts";

type Priority = "P1" | "P2" | "P3";

type FormValues = {
  name: string;
  summary: string;
  priority: Priority | undefined;
  priorityReason: string;
  accountAtRisk: boolean;
  problem: string;
  context: string;
  researchDone: string;
  replicated: boolean;
  replicateResult: string;
  checkedConfig: boolean;
  confirmedDataDiscrepancy: boolean;
  hypothesis: string;
};

const DEFAULT_FORM_DATA: FormValues = {
  name: "",
  summary: "",
  priority: undefined,
  priorityReason: "",
  accountAtRisk: false,
  problem: "",
  context: "",
  researchDone: "",
  replicated: false,
  replicateResult: "",
  checkedConfig: false,
  confirmedDataDiscrepancy: false,
  hypothesis: "",
};

const requiredText = (message: string) => ({ required: { value: true, message } });

export const BugReportModal = NiceModal.create(() => {
  const modal = useModal();
  const { t } = useTranslation(["admin", "common"]);

  const user = useAppSelector((state) => state.user);
  const marketplace = useAppSelector((state) => state.marketPlace.marketPlace);
  const channel = useAppSelector((state) => state.channel.currentChannel);

  const submitMutation = useSubmitBugReport();

  const { control, handleSubmit, formState } = useForm<FormValues>({
    defaultValues: { ...DEFAULT_FORM_DATA, name: user.user_full_name },
  });

  const PRIORITY_OPTIONS = [
    { label: t(($) => $.admin.bugReport["P3 — Everything else (reporting, old data, minor UI bugs)"]), value: "P3" },
    { label: t(($) => $.admin.bugReport["P2 — Actively impacting platform usage"]), value: "P2" },
    { label: t(($) => $.admin.bugReport["P1 — Actively impacting sales"]), value: "P1" },
  ] as const;

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    if (!values.priority) return;
    const priority: Priority = values.priority;

    const meta = {
      user_id: user.user_id,
      email: user.email,
      user_full_name: values.name,
      organization_id: user.organization_id,
      organization_name: user.organization_name,
      organization_type: user.organization_type,
      customer_type: user.customer_type,
      profile: user.current_profile,
      marketplace,
      channel,
      time: new Date().toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      browser_info: {
        language: navigator.language,
        platform: navigator.platform,
        userAgent: navigator.userAgent,
        vendor: navigator.vendor,
      },
    };

    const channelLabel = channel ? channel.charAt(0).toUpperCase() + channel.slice(1) : "";
    const title = `${user.organization_name}(${user.organization_id}) - ${marketplace} - ${channelLabel} - ${priority} - ${values.summary}`;

    submitMutation.mutate(
      {
        title,
        summary: values.summary,
        priority,
        effective_priority: priority,
        priority_reason: values.priorityReason,
        account_at_risk: values.accountAtRisk,
        problem: values.problem,
        context: values.context,
        current_url: window.location.href,
        research_done: values.researchDone,
        troubleshooting: {
          replicated: values.replicated,
          replicate_result: values.replicateResult,
          checked_config: values.checkedConfig,
          confirmed_data_discrepancy: values.confirmedDataDiscrepancy,
        },
        hypothesis: values.hypothesis,
        meta,
      },
      {
        onSuccess: () => {
          successToast({ message: t(($) => $.admin.bugReport["Bug report successfully submitted"]) });
          modal.remove();
        },
      },
    );
  };

  const onCancel = () => {
    modal.remove();
  };

  return (
    <Modal
      isOpen={modal.visible}
      isDismissable
      onOpenChange={(isOpen) => {
        if (!isOpen) modal.remove();
      }}
      title={t(($) => $.admin.bugReport["Submit a Bug Report"])}
      css={{
        width: "min(800px, 90vw)",
        overflowY: "auto",
        "&::-webkit-scrollbar-track": {
          boxShadow: "none",
          backgroundColor: "rgb(255 255 255 / 100%)",
          padding: "10px",
          margin: "10px 0 50px",
          maxHeight: "90%",
        },
        "&::-webkit-scrollbar": {
          width: "5px",
          height: "15px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#623f7f",
          border: "none",
          height: "10px",
        },
      }}
    >
      <VStack>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: "medium",
            alignItems: "stretch",
            width: "100%",
            height: "100%",
            paddingX: "xlarge",
          })}
        >
          <Controller
            control={control}
            name={"name"}
            rules={requiredText(t(($) => $.admin.bugReport["Your name is required"]))}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label={t(($) => $.admin.bugReport["Your Name"])}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name={"summary"}
            rules={requiredText(t(($) => $.admin.bugReport["A short summary is required"]))}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label={t(($) => $.admin.bugReport["Issue Summary"])}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name={"priority"}
            rules={{ validate: (v) => v != null || t(($) => $.admin.bugReport["Priority is required"]) }}
            render={({ field, fieldState }) => (
              <Select
                label={t(($) => $.admin.bugReport.Priority)}
                onSelectionChange={(value) => field.onChange(value)}
                selectedKey={field.value ?? null}
                items={PRIORITY_OPTIONS}
                errorMessage={fieldState.error?.message}
              />
            )}
          />

          <Controller
            control={control}
            name={"priorityReason"}
            rules={requiredText(t(($) => $.admin.bugReport["A one-sentence reason for the priority is required"]))}
            render={({ field, fieldState }) => (
              <TextAreaField
                rows={2}
                {...field}
                label={t(($) => $.admin.bugReport["Reason for Priority"])}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name={"accountAtRisk"}
            render={({ field }) => (
              <Checkbox
                value={field.value}
                onChange={field.onChange}
                label={t(($) => $.admin.bugReport["Account at risk or within first 30 days"])}
              />
            )}
          />
          <Controller
            control={control}
            name={"problem"}
            rules={requiredText(t(($) => $.admin.bugReport["Describe the problem"]))}
            render={({ field, fieldState }) => (
              <TextAreaField
                rows={2}
                {...field}
                label={t(($) => $.admin.bugReport.Problem)}
                description={t(
                  ($) =>
                    $.admin.bugReport[
                      "One sentence — what you/the brand expected to happen vs. what is actually happening"
                    ],
                )}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name={"context"}
            rules={requiredText(t(($) => $.admin.bugReport["Context is required"]))}
            render={({ field, fieldState }) => (
              <TextAreaField
                rows={2}
                {...field}
                label={t(($) => $.admin.bugReport.Context)}
                description={t(
                  ($) =>
                    $.admin.bugReport[
                      "Link to account, campaign ID, etc. The current page URL is attached automatically."
                    ],
                )}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name={"researchDone"}
            rules={requiredText(t(($) => $.admin.bugReport["Research done is required"]))}
            render={({ field, fieldState }) => (
              <TextAreaField
                rows={2}
                {...field}
                label={t(($) => $.admin.bugReport["Research Done"])}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
          <VStack
            css={{
              gap: "small",
              border: "1px solid {colors.border.default}",
              borderRadius: "standard",
              padding: "medium",
              alignItems: "start",
            }}
          >
            <label className={css({ fontWeight: "bold" })}>{t(($) => $.admin.bugReport["My Troubleshooting"])}</label>
            <span className={css({ fontSize: "small", color: "text.muted" })}>
              {t(($) => $.admin.bugReport["Confirm at least one of the following:"])}
            </span>
            <Controller
              control={control}
              name={"replicated"}
              rules={{
                validate: (_v, values) =>
                  values.replicated ||
                  values.checkedConfig ||
                  values.confirmedDataDiscrepancy ||
                  t(($) => $.admin.bugReport["Confirm at least one troubleshooting step"]),
                deps: ["replicateResult"],
              }}
              render={({ field }) => (
                <Checkbox
                  value={field.value}
                  onChange={field.onChange}
                  label={t(($) => $.admin.bugReport["I tried to replicate it"])}
                />
              )}
            />
            <Controller
              control={control}
              name={"replicateResult"}
              rules={{
                validate: (v, values) =>
                  !values.replicated ||
                  (v.trim().length > 0 ?
                    true
                  : t(($) => $.admin.bugReport["Describe what happened when you tried to replicate"])),
              }}
              render={({ field, fieldState }) => (
                <TextAreaField
                  rows={2}
                  {...field}
                  label={t(($) => $.admin.bugReport["Replication Result"])}
                  errorMessage={fieldState.error?.message}
                  className={css({ width: "100%" })}
                />
              )}
            />
            <Controller
              control={control}
              name={"checkedConfig"}
              rules={{ deps: ["replicated"] }}
              render={({ field }) => (
                <Checkbox
                  value={field.value}
                  onChange={field.onChange}
                  label={t(($) => $.admin.bugReport["I checked the Configuration / API Connections page"])}
                />
              )}
            />
            <Controller
              control={control}
              name={"confirmedDataDiscrepancy"}
              rules={{ deps: ["replicated"] }}
              render={({ field }) => (
                <Checkbox
                  value={field.value}
                  onChange={field.onChange}
                  label={t(
                    ($) => $.admin.bugReport["I confirmed data discrepancy in Seller / Ads Console (if data related)"],
                  )}
                />
              )}
            />
            {formState.errors.replicated?.message && (
              <span className={css({ color: "error", fontSize: "small", textAlign: "start" })}>
                {formState.errors.replicated.message}
              </span>
            )}
          </VStack>
          <Controller
            control={control}
            name={"hypothesis"}
            rules={requiredText(t(($) => $.admin.bugReport["A hypothesis is required"]))}
            render={({ field, fieldState }) => (
              <TextAreaField
                rows={2}
                {...field}
                label={t(($) => $.admin.bugReport["My Hypothesis"])}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
          <HStack gap={"large"} justifyContent={"center"} paddingY={"medium"}>
            <Button variant={"secondary"} onPress={onCancel} isDisabled={submitMutation.isPending}>
              {t(($) => $.common.actions.Cancel)}
            </Button>
            <Button type={"submit"} isDisabled={submitMutation.isPending || !formState.isValid}>
              {submitMutation.isPending ?
                t(($) => $.admin.bugReport["Submitting…"])
              : t(($) => $.admin.bugReport["Submit Bug Report"])}
            </Button>
          </HStack>
        </form>
      </VStack>
    </Modal>
  );
});
